/**
 * 메뉴 권한 서비스
 *
 * 메뉴 권한 관련 API 호출 및 데이터 처리
 */

import apiClient from './api';
import type {
  MenuPermission,
  MenuPermissionListParams,
  SetMenuPermissionRequest,
  AdminMenuPermissionResponse,
} from '../types/menuPermission';

/**
 * 메뉴 권한 목록 조회
 *
 * @param params - 조회 파라미터
 * @returns 메뉴 권한 목록
 */
export const getMenuPermissions = async (
  params?: MenuPermissionListParams
): Promise<{ success: boolean; data: { permissions: MenuPermission[] } }> => {
  const response = await apiClient.get('/admin/menu-permissions', { params });
  return response.data;
};

/**
 * 메뉴 권한 상세 조회
 *
 * @param id - 권한 ID
 * @returns 메뉴 권한 정보
 */
export const getMenuPermission = async (
  id: number
): Promise<{ success: boolean; data: { permission: MenuPermission } }> => {
  const response = await apiClient.get(`/admin/menu-permissions/${id}`);
  return response.data;
};

/**
 * 관리자의 메뉴 권한 조회
 *
 * @param adminId - 관리자 ID
 * @param menuId - 메뉴 ID
 * @returns 관리자의 메뉴 권한 정보
 */
export const getAdminMenuPermission = async (
  adminId: number,
  menuId: number
): Promise<{ success: boolean; data: { permission: AdminMenuPermissionResponse } }> => {
  const response = await apiClient.get(`/admin/menu-permissions/admin/${adminId}/menu/${menuId}`);
  return response.data;
};

/**
 * 메뉴 권한 설정 (롤별 또는 개별 관리자별)
 *
 * @param data - 권한 설정 데이터
 * @returns 설정된 권한 정보
 */
export const setMenuPermission = async (
  data: SetMenuPermissionRequest
): Promise<{ success: boolean; data: { permission: MenuPermission } }> => {
  const response = await apiClient.post('/admin/menu-permissions', data);
  return response.data;
};

/**
 * 메뉴 권한 수정
 *
 * @param id - 권한 ID
 * @param data - 수정할 데이터
 * @returns 수정된 권한 정보
 */
export const updateMenuPermission = async (
  id: number,
  data: Partial<SetMenuPermissionRequest>
): Promise<{ success: boolean; data: { permission: MenuPermission } }> => {
  const response = await apiClient.put(`/admin/menu-permissions/${id}`, data);
  return response.data;
};

/**
 * 메뉴 권한 삭제
 *
 * @param id - 권한 ID
 * @returns 성공 메시지
 */
export const deleteMenuPermission = async (
  id: number
): Promise<{ success: boolean; message: string }> => {
  const response = await apiClient.delete(`/admin/menu-permissions/${id}`);
  return response.data;
};
